import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  createAdminPromocode,
  deleteAdminPromocode,
  getAdminPromocodes,
  type AdminPromocode,
  updateAdminPromocode,
} from '../../api/admin.api';
import DangerActionModal from '../../components/DangerActionModal';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import { ADMIN_PERMISSIONS } from '../../config/admin-permissions';
import './admin.css';

type Draft = {
  code: string;
  description: string;
  discountType: 'percent' | 'fixed';
  discountValue: string;
  minOrderSum: string;
  usageLimit: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
};

const emptyDraft: Draft = {
  code: '',
  description: '',
  discountType: 'percent',
  discountValue: '',
  minOrderSum: '0',
  usageLimit: '',
  startsAt: '',
  endsAt: '',
  isActive: true,
};

function toDatetimeLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

export default function AdminPromocodes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<AdminPromocode[]>([]);
  const [editing, setEditing] = useState<AdminPromocode | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [notice, setNotice] = useState<string | null>(null);
  const [dangerRow, setDangerRow] = useState<AdminPromocode | null>(null);
  const [dangerLoading, setDangerLoading] = useState(false);
  const [dangerError, setDangerError] = useState<string | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | null>(null);
  const { hasPermission } = useAdminPermissions();
  const canCreate = hasPermission(ADMIN_PERMISSIONS.PROMOCODES_CREATE);
  const canUpdate = hasPermission(ADMIN_PERMISSIONS.PROMOCODES_UPDATE);
  const canDelete = hasPermission(ADMIN_PERMISSIONS.PROMOCODES_DELETE);
  const [compactView, setCompactView] = useState<boolean>(() => window.innerWidth <= 900);
  useEffect(() => {
    const onResize = () => setCompactView(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminPromocodes();
      setRows(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить промокоды');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    const id = Number(searchParams.get('promocodeId'));
    if (!Number.isInteger(id) || id <= 0 || rows.length === 0) return;
    const target = rows.find((row) => row.id === id);
    if (target) startEdit(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, searchParams]);

  const title = useMemo(
    () => (editing ? `Редактирование: ${editing.code}` : 'Создать промокод'),
    [editing]
  );

  const resetForm = () => {
    setEditing(null);
    setDraft(emptyDraft);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('promocodeId');
      return next;
    });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = draft.code.trim().toUpperCase();
    const discountValue = Number(draft.discountValue);
    const minOrderSum = Number(draft.minOrderSum || 0);
    const usageLimit = draft.usageLimit.trim() ? Number(draft.usageLimit.trim()) : null;
    if (!code) return setError('Введите код промокода');
    if (!Number.isFinite(discountValue) || discountValue <= 0) return setError('Скидка должна быть больше 0');
    if (draft.discountType === 'percent' && discountValue > 100) return setError('Процент не может быть больше 100');
    if (usageLimit != null && (!Number.isFinite(usageLimit) || usageLimit < 1)) {
      return setError('Лимит использования должен быть >= 1');
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        code,
        description: draft.description.trim(),
        discountType: draft.discountType,
        discountValue,
        minOrderSum: Number.isFinite(minOrderSum) ? Math.max(0, minOrderSum) : 0,
        usageLimit,
        startsAt: draft.startsAt || null,
        endsAt: draft.endsAt || null,
        isActive: draft.isActive,
      };
      if (editing) {
        await updateAdminPromocode(editing.id, payload);
      } else {
        await createAdminPromocode(payload);
      }
      resetForm();
      await load();
      setNotice('Промокод сохранен.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить промокод');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row: AdminPromocode) => {
    setEditing(row);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('promocodeId', String(row.id));
      return next;
    });
    setDraft({
      code: row.code,
      description: row.description || '',
      discountType: row.discountType,
      discountValue: String(row.discountValue),
      minOrderSum: String(row.minOrderSum ?? 0),
      usageLimit: row.usageLimit == null ? '' : String(row.usageLimit),
      startsAt: toDatetimeLocal(row.startsAt),
      endsAt: toDatetimeLocal(row.endsAt),
      isActive: row.isActive,
    });
  };

  const toggleActive = async (row: AdminPromocode) => {
    if (statusUpdatingId === row.id) return;
    try {
      setStatusUpdatingId(row.id);
      await updateAdminPromocode(row.id, { isActive: !row.isActive });
      await load();
      setNotice('Статус промокода обновлен. Действие зафиксировано в журнале.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось изменить статус');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const remove = async (row: AdminPromocode) => {
    setDangerError(null);
    setDangerRow(row);
  };

  return (
    <div className="admin-page">
      {error && <div className="admin-error">{error}</div>}
      {notice && <div className="admin-card">{notice}</div>}

      <form onSubmit={onSubmit} className="admin-card admin-editor-form" style={{ marginBottom: '16px' }}>
        <h2 className="admin-card-title">{title}</h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '10px' }}>
          <label className="admin-field">
            <span className="admin-field-label">Код</span>
            <input
              className="admin-input"
              data-testid="admin-promocode-code-input"
              value={draft.code}
              onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
              placeholder="WELCOME10"
              required
            />
          </label>
          <label className="admin-field">
            <span className="admin-field-label">Тип скидки</span>
            <select
              className="admin-input"
              value={draft.discountType}
              onChange={(e) => setDraft((d) => ({ ...d, discountType: e.target.value as 'percent' | 'fixed' }))}
            >
              <option value="percent">Процент (%)</option>
              <option value="fixed">Фикс (руб)</option>
            </select>
          </label>
          <label className="admin-field">
            <span className="admin-field-label">Скидка</span>
            <input
              className="admin-input"
              type="number"
              min={0}
              value={draft.discountValue}
              onChange={(e) => setDraft((d) => ({ ...d, discountValue: e.target.value }))}
              required
            />
          </label>
          <label className="admin-field">
            <span className="admin-field-label">Мин. сумма заказа</span>
            <input
              className="admin-input"
              type="number"
              min={0}
              value={draft.minOrderSum}
              onChange={(e) => setDraft((d) => ({ ...d, minOrderSum: e.target.value }))}
            />
          </label>
          <label className="admin-field">
            <span className="admin-field-label">Лимит использований</span>
            <input
              className="admin-input"
              type="number"
              min={1}
              value={draft.usageLimit}
              onChange={(e) => setDraft((d) => ({ ...d, usageLimit: e.target.value }))}
              placeholder="без лимита"
            />
          </label>
          <label className="admin-field">
            <span className="admin-field-label">Старт</span>
            <input
              className="admin-input"
              type="datetime-local"
              value={draft.startsAt}
              onChange={(e) => setDraft((d) => ({ ...d, startsAt: e.target.value }))}
            />
          </label>
          <label className="admin-field">
            <span className="admin-field-label">Окончание</span>
            <input
              className="admin-input"
              type="datetime-local"
              value={draft.endsAt}
              onChange={(e) => setDraft((d) => ({ ...d, endsAt: e.target.value }))}
            />
          </label>
        </div>

        <label className="admin-field">
          <span className="admin-field-label">Описание</span>
          <textarea
            className="admin-input"
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            placeholder="Краткое описание для админки"
          />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={draft.isActive}
            onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
          />
          Активен
        </label>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button type="submit" className="admin-btn admin-btn-primary" disabled={!canCreate && !editing ? true : !canUpdate && !!editing ? true : saving}>
            {saving ? 'Сохраняем...' : editing ? 'Сохранить' : 'Создать'}
          </button>
          {editing && (
            <button type="button" className="admin-btn admin-btn-secondary" onClick={resetForm}>
              Отмена
            </button>
          )}
        </div>
      </form>

      {compactView ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map((row) => (
            <div key={row.id} className="admin-card">
              <div style={{ fontWeight: 700 }}>{row.code}</div>
              <div>{row.discountType === 'percent' ? `${row.discountValue}%` : `${row.discountValue} ₽`}</div>
              <div>Мин. сумма: {row.minOrderSum} ₽</div>
              <div>Использовано: {row.usageCount}{row.usageLimit != null ? ` / ${row.usageLimit}` : ''}</div>
              <div>Статус: {row.isActive ? 'Активен' : 'Выключен'}</div>
              <div className="admin-cell-actions">
                <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={!canUpdate} onClick={() => startEdit(row)}>Ред.</button>
                <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={!canUpdate || statusUpdatingId === row.id} onClick={() => toggleActive(row)}>{statusUpdatingId === row.id ? '...' : row.isActive ? 'Выкл' : 'Вкл'}</button>
                <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={!canDelete} onClick={() => remove(row)} style={{ color: 'red' }}>Удал.</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th className="admin-th">Код</th>
              <th className="admin-th">Скидка</th>
              <th className="admin-th admin-num">Мин. сумма</th>
              <th className="admin-th admin-num">Использовано</th>
              <th className="admin-th admin-center">Статус</th>
              <th className="admin-th admin-center">Действия</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="admin-td" colSpan={6}>Загрузка...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="admin-td" colSpan={6}>Промокодов пока нет</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="admin-table-row">
                  <td className="admin-td">
                    <div style={{ fontWeight: 600 }}>{row.code}</div>
                    {row.description ? (
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{row.description}</div>
                    ) : null}
                  </td>
                  <td className="admin-td">{row.discountType === 'percent' ? `${row.discountValue}%` : `${row.discountValue} ₽`}</td>
                  <td className="admin-td admin-num admin-mono">{row.minOrderSum} ₽</td>
                  <td className="admin-td admin-num admin-mono">
                    {row.usageCount}{row.usageLimit != null ? ` / ${row.usageLimit}` : ''}
                  </td>
                  <td className="admin-td admin-center">{row.isActive ? 'Активен' : 'Выключен'}</td>
                  <td className="admin-td admin-center">
                    <div className="admin-cell-actions admin-cell-actions-center">
                      <button className="admin-btn admin-btn-secondary admin-btn-sm" data-testid={`admin-promocode-edit-${row.id}`} disabled={!canUpdate} onClick={() => startEdit(row)}>
                        Ред.
                      </button>
                      <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={!canUpdate || statusUpdatingId === row.id} onClick={() => toggleActive(row)}>
                        {statusUpdatingId === row.id ? '...' : row.isActive ? 'Выкл' : 'Вкл'}
                      </button>
                      <button className="admin-btn admin-btn-secondary admin-btn-sm" data-testid={`admin-promocode-delete-${row.id}`} disabled={!canDelete} onClick={() => remove(row)} style={{ color: 'red' }}>
                        Удал.
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}
      <DangerActionModal
        open={dangerRow != null}
        title="Удаление промокода"
        description="Промокод будет удален без возможности восстановления."
        entityLabel={dangerRow?.code ?? ''}
        confirmationText="DELETE"
        requireReason
        loading={dangerLoading}
        error={dangerError}
        onClose={() => {
          if (!dangerLoading) setDangerRow(null);
        }}
        onConfirm={async ({ reason }) => {
          if (!dangerRow) return;
          setDangerLoading(true);
          setDangerError(null);
          try {
            await deleteAdminPromocode(dangerRow.id, reason);
            await load();
            setDangerRow(null);
            setNotice(reason ? 'Промокод удален. Причина и действие зафиксированы в журнале.' : 'Промокод удален. Действие зафиксировано в журнале.');
          } catch (e) {
            setDangerError(e instanceof Error ? e.message : 'Не удалось удалить промокод');
          } finally {
            setDangerLoading(false);
          }
        }}
      />
    </div>
  );
}

